import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, ArrowRight } from "lucide-react";
import { api, fmtBytes } from "../lib/api";
import { GlassCard, PageHeader, QualityBadge, VersionBadge, EmptyState, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";

export default function Upgrades() {
  const [reports, setReports] = useState(null);
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    api.get("/vod/upgrades").then((r) => setReports(r.data)).catch(() => setReports([]));
    api.get("/vod/low-quality").then((r) => setQueue(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="upgrades-page">
      <PageHeader eyebrow="Curadoria automática" title="Upgrades de Qualidade" subtitle="Relatório de todas as substituições automáticas e a fila de versões CAM/TS aguardando a digital." />

      <TutorialHint
        id="upgrades"
        title="Como funciona o auto-replace"
        steps={[
          "Versões CAM/TS entram automaticamente na fila de substituição.",
          "Quando sobe a mesma versão (ex: Legendado) em qualidade superior, o ficheiro antigo é substituído.",
          "Cada troca gera um relatório com data, delta de tamanho e motivo.",
          "Dublado e Legendado são chaves independentes — um nunca apaga o outro.",
        ]}
      />

      {queue.length > 0 && (
        <GlassCard className="p-6 border-rose-500/30 anim-fade-up" data-testid="low-quality-queue">
          <div className="flex items-center gap-2.5 mb-4">
            <AlertTriangle size={17} className="text-rose-400" />
            <h3 className="font-display text-white font-semibold text-sm">Fila de substituição automática ({queue.length})</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {queue.map((v) => (
              <div key={v.id} className="flex items-center gap-2.5 p-3 rounded-xl bg-rose-500/5 border border-rose-500/20" data-testid={`queue-item-${v.id}`}>
                <span className="text-sm text-white font-medium truncate">{v.title}</span>
                <VersionBadge version={v.version} />
                <QualityBadge quality={v.quality} />
                <span className="text-[10px] text-rose-300/80 ml-auto font-mono2">AGUARDANDO DIGITAL</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {reports === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : reports.length === 0 ? (
        <EmptyState icon={RefreshCw} title="Nenhum upgrade ainda" subtitle="Quando uma versão for substituída por qualidade superior, o relatório aparece aqui." testid="upgrades-empty" />
      ) : (
        <GlassCard className="overflow-hidden anim-fade-up anim-delay-1" data-testid="upgrades-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-5 py-4">Título</th>
                  <th className="px-5 py-4">Versão</th>
                  <th className="px-5 py-4">Troca</th>
                  <th className="px-5 py-4">Tamanho</th>
                  <th className="px-5 py-4">Motivo</th>
                  <th className="px-5 py-4">Data</th>
                </tr>
              </thead>
              <tbody data-testid="upgrades-table-body">
                {reports.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/40 last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`upgrade-row-${r.id}`}>
                    <td className="px-5 py-4 text-white font-medium">{r.title}{r.season != null && <span className="text-slate-500 text-xs ml-1.5">S{String(r.season).padStart(2, "0")}E{String(r.episode).padStart(2, "0")}</span>}</td>
                    <td className="px-5 py-4"><VersionBadge version={r.version} /></td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-1.5">
                        <QualityBadge quality={r.old_quality} />
                        <ArrowRight size={13} className="text-slate-500" />
                        <QualityBadge quality={r.new_quality} />
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-400 font-mono2">{fmtBytes(r.old_size_bytes)} → {fmtBytes(r.new_size_bytes)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-mono2 uppercase tracking-wider ${r.reason === "auto_replace_low_quality" ? "text-rose-300" : "text-cyan-300"}`}>
                        {r.reason === "auto_replace_low_quality" ? "Auto-replace CAM/TS" : "Upgrade manual"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </div>
  );
}
